import { useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, Check, RotateCcw, Search } from 'lucide-react';
import { parseFileUrl } from '../lib/parse';

export function ProgressBar({ value, completed = false }: { value: number; completed?: boolean }) {
  return <div className={`progress-track ${completed ? 'is-complete' : ''}`}><span style={{ transform: `scaleX(${Math.max(0, Math.min(100, value)) / 100})` }} /></div>;
}

export function ImageWithFallback({ src, alt = '', className, fallback }: { src: unknown; alt?: string; className?: string; fallback: ReactNode }) {
  const url = parseFileUrl(src);
  const [failedUrl, setFailedUrl] = useState<string>();
  if (!url || failedUrl === url) return <>{fallback}</>;
  return <img src={url} alt={alt} className={className} onError={() => setFailedUrl(url)} />;
}

export function BrandLogo({ src, className }: { src: unknown; className: string }) {
  return <span className={className}><ImageWithFallback src={src ?? '/ayurguide-logo.png'} alt="AyurGuide logo" className="brand-image" fallback={<img src="/ayurguide-logo.png" alt="AyurGuide logo" className="brand-image" />} /></span>;
}

export function ProfileAvatar({ src, initials, className }: { src?: unknown; initials: string; className: string }) {
  return <span className={className}><ImageWithFallback src={src} alt="" className="profile-avatar-image" fallback={initials} /></span>;
}

export function CourseArtwork({ index = 0, icon = 'leaf', image }: { index?: number; icon?: string; image?: unknown }) {
  return <div className={`course-art art-${index % 3} art-kind-${icon}`}>
    <ImageWithFallback src={image} alt="" className="course-art-image" fallback={<img src="/ayurguide-logo.png" alt="" className="course-art-brand-image" />} />
  </div>;
}

export function EmptyState({ icon: Icon = Search, title, description, action }: { icon?: LucideIcon; title: string; description: string; action?: React.ReactNode }) {
  return <div className="empty-state"><div className="empty-icon"><Icon size={28} /></div><h3>{title}</h3><p>{description}</p>{action}</div>;
}

export function Badge({ children, tone = 'teal' }: { children: React.ReactNode; tone?: 'teal' | 'green' | 'amber' | 'red' | 'gray' }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function Button({ children, variant = 'primary', icon: Icon, className = '', ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'soft' | 'ghost' | 'danger'; icon?: LucideIcon }) {
  return <button className={`btn btn-${variant} ${className}`} {...props}>{Icon && <Icon size={16} />}{children}</button>;
}

export function SectionHeading({ title, action, actionLabel = 'View all' }: { title: string; action?: () => void; actionLabel?: string }) {
  return <div className="section-heading"><h2>{title}</h2>{action && <button className="text-button" onClick={action}>{actionLabel}<ArrowRight size={14} /></button>}</div>;
}

export function LoadingState() {
  return <div className="loading-list">{[1, 2, 3].map((item) => <div className="skeleton-card" key={item}><div className="skeleton skeleton-art" /><div className="skeleton-copy"><div className="skeleton skeleton-line wide" /><div className="skeleton skeleton-line" /><div className="skeleton skeleton-line small" /></div></div>)}</div>;
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return <EmptyState icon={RotateCcw} title="Unable to load this right now" description="We couldn't reach the learning service. Your data is safe — try again in a moment." action={onRetry && <Button variant="soft" onClick={onRetry}>Try again</Button>} />;
}

export function SearchField({ value, onChange, placeholder = 'Search' }: { value: string; onChange: (value: string) => void; placeholder?: string }) {
  return <label className="search-field"><Search size={18} /><input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} aria-label={placeholder} />{value && <button onClick={() => onChange('')} aria-label="Clear search">×</button>}</label>;
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="section-label">{children}</div>;
}

export function StatePill({ completed, value }: { completed?: boolean; value: number }) {
  return completed ? <Badge tone="green"><Check size={12} /> Completed</Badge> : <span className={`progress-label ${value === 0 ? 'muted' : ''}`}>{value}% complete</span>;
}
